import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useSignetStudio } from './SignetStudioContext';
import { ACTIONS } from './reducers/studioReducer';
import ShapeAccordion from './components/ShapeAccordion';
import { getConfigSwitchLabel } from './components/ShapeAccordion';
const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
}));

const ApprovalMapping = () => {
  const classes = useStyles();
  const { state, dispatch } = useSignetStudio();
  const [expanded, setExpanded] = React.useState(null);

  const configSwitchLabel = getConfigSwitchLabel(state);

  const handleAccordionChange = (shapeId) => (event, isExpanded) => {
    setExpanded(isExpanded ? shapeId : null);
  };

  const handleShapeToggle = (shapeId, checked) => {
    const updatedData = {
      ...state.mappingData,
      shapes: state.mappingData.shapes.map((shape) => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            checked,
            children: shape.children.map((size) => ({
              ...size,
              checked: checked ? size.checked : false,
              children: size.children.map(shank => ({
                ...shank,
                checked: checked ? shank.checked : false
              }))
            })),
          };
        }
        return shape;
      }),
    };
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedData });

    if (!checked && expanded === shapeId) {
      setExpanded(null);
    }
  };

  const handleSizeToggle = (shapeId, sizeId, checked) => {
    const updatedData = {
      ...state.mappingData,
      shapes: state.mappingData.shapes.map((shape) => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            children: shape.children.map((size) => {
              if (size.id === sizeId) {
                return {
                  ...size,
                  checked,
                  children: size.children.map(shank => ({
                    ...shank,
                    checked: checked ? shank.checked : false
                  }))
                };
              }
              return size;
            }),
          };
        }
        return shape;
      }),
    };
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedData });
  };

  const handleShankToggle = (shapeId, sizeId, shankSku, checked) => {
    const updatedData = {
      ...state.mappingData,
      shapes: state.mappingData.shapes.map((shape) => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            children: shape.children.map((size) => {
              if (size.id === sizeId) {
                return {
                  ...size,
                  children: size.children.map(shank => {
                    if (shank.sku === shankSku) {
                      return { ...shank, checked };
                    }
                    return shank;
                  })
                };
              }
              return size;
            }),
          };
        }
        return shape;
      }),
    };
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedData });
  };

  const handleShapeScaleChange = (shapeId, difference, event) => {
    event?.stopPropagation();

    const currentShape = state.mappingData.shapes.find((s) => s.id === shapeId);
    
    // Update shape configs
    const updatedConfigs = state.shapeConfigs.map((config) => {
      if (config.shape_sku === currentShape?.sku) {
        const updatedSizes = config.sizes.map((size) => {
          const currentSize = currentShape.children.find((s) => s?.sku === size.size_sku);
          const isEnabled = currentSize?.checked;

          if (isEnabled) {
            return {
              ...size,
              shanks: size.shanks.map((shank) => {
                const currentShank = currentSize.children.find(
                  (s) => s.sku === shank.shank_sku
                );
                const isShankEnabled = currentShank?.checked;
                
                if (isShankEnabled) {
                  return {
                    ...shank,
                    config: {
                      ...shank.config,
                      [configSwitchLabel]: {
                        ...shank.config?.[configSwitchLabel],
                        scale: Math.max(-50, Math.min(50, (shank.config?.[configSwitchLabel]?.scale || 0) + difference))
                      }
                    }
                  };
                }
                return shank;
              }),
            };
          }
          return size;
        });

        return {
          ...config,
          sizes: updatedSizes,
        };
      }
      return config;
    });

    // Update mapping data
    const updatedMappingData = {
      ...state.mappingData,
      shapes: state.mappingData.shapes.map((shape) => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            children: shape.children.map((size) => {
              return {
                ...size,
                children: size.children.map(shank => {
                  if (shank.checked && size.checked) {
                    return {
                      ...shank,
                      config: {
                        ...shank.config,
                        [configSwitchLabel]: {
                          ...shank.config?.[configSwitchLabel],
                          scale: Math.max(-50, Math.min(50, (shank.config?.[configSwitchLabel]?.scale || 0) + difference))
                        }
                      }
                    };
                  }
                  return shank;
                })
              };
            }),
          };
        }
        return shape;
      }),
    };

    dispatch({ type: ACTIONS.UPDATE_SHAPE_CONFIGS, payload: updatedConfigs });
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedMappingData });
  };

  const handleSizeScaleChange = (shapeId, sizeId, shankSku, newValue) => {
    // Update shape configs
    const updatedConfigs = state.shapeConfigs.map((config) => {
      const currentShape = state.mappingData.shapes.find((s) => s.id === shapeId);
      if (config.shape_sku === currentShape?.sku) {
        const currentSize = currentShape?.children?.find((s) => s.id === sizeId);
        const isEnabled = currentSize?.checked;

        const updatedSizes = config.sizes.map((size) => {
          if (size.size_sku === currentSize?.sku && isEnabled) {
            return {
              ...size,
              shanks: size.shanks.map((shank) => {
                if (shank.shank_sku === shankSku) {
                  return { 
                    ...shank, 
                    config: {
                      ...shank.config,
                      [configSwitchLabel]: {
                        ...shank.config?.[configSwitchLabel],
                        scale: Math.max(-50, Math.min(50, newValue))
                      }
                    }
                  };
                }
                return shank;
              }),
            };
          }
          return size;
        });

        return {
          ...config,
          sizes: updatedSizes,
        };
      }
      return config;
    });

    // Update mapping data
    const updatedMappingData = {
      ...state.mappingData,
      shapes: state.mappingData.shapes.map((shape) => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            children: shape.children.map((size) => {
              if (size.id === sizeId) {
                return {
                  ...size,
                  children: size.children.map(shank => {
                    if (shank.sku === shankSku) {
                      return { 
                        ...shank, 
                        config: {
                          ...shank.config,
                          [configSwitchLabel]: {
                            ...shank.config?.[configSwitchLabel],
                            scale: Math.max(-50, Math.min(50, newValue))
                          }
                        }
                      };
                    }
                    return shank;
                  })
                };
              }
              return size;
            }),
          };
        }
        return shape;
      }),
    };

    dispatch({ type: ACTIONS.UPDATE_SHAPE_CONFIGS, payload: updatedConfigs });
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedMappingData });
  };

  const handleShapePaddingChange = (shapeId, difference, event) => {
    event?.stopPropagation();

    const currentShape = state.mappingData.shapes.find((s) => s.id === shapeId);
    
    // Update shape configs
    const updatedConfigs = state.shapeConfigs.map((config) => {
      if (config.shape_sku === currentShape?.sku) {
        const updatedSizes = config.sizes.map((size) => {
          const currentSize = currentShape.children.find((s) => s?.sku === size.size_sku);
          const isEnabled = currentSize?.checked;

          if (isEnabled) {
            return {
              ...size,
              shanks: size.shanks.map((shank) => {
                const currentShank = currentSize.children.find(
                  (s) => s.sku === shank.shank_sku
                );
                const isShankEnabled = currentShank?.checked;
                
                if (isShankEnabled) {
                  return {
                    ...shank,
                    config: {
                      ...shank.config,
                      [configSwitchLabel]: {
                        ...shank?.config?.[configSwitchLabel],
                        padding: Math.max(0, Math.min(50, (shank?.config?.[configSwitchLabel]?.padding || 0) + difference))
                      }
                    }
                  };
                }
                return shank;
              }),
            };
          }
          return size;
        });

        return {
          ...config,
          sizes: updatedSizes,
        };
      }
      return config;
    });

    // Update mapping data
    const updatedMappingData = {
      ...state.mappingData,
      shapes: state.mappingData.shapes.map((shape) => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            children: shape.children.map((size) => {
              return {
                ...size,
                children: size.children.map(shank => {
                  if (shank.checked && size.checked) {
                    return {
                      ...shank,
                      config: {
                        ...shank.config,
                        [configSwitchLabel]: {
                          ...shank.config?.[configSwitchLabel],
                          padding: Math.max(0, Math.min(50, (shank.config?.[configSwitchLabel]?.padding || 0) + difference))
                        }
                      }
                    };
                  }
                  return shank;
                })
              };
            }),
          };
        }
        return shape;
      }),
    };

    dispatch({ type: ACTIONS.UPDATE_SHAPE_CONFIGS, payload: updatedConfigs });
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedMappingData });
  };

  const handleSizePaddingChange = (shapeId, sizeId, shankSku, newValue) => {
    // Update shape configs
    const updatedConfigs = state.shapeConfigs.map((config) => {
      const currentShape = state.mappingData.shapes.find((s) => s.id === shapeId);
      if (config.shape_sku === currentShape?.sku) {
        const currentSize = currentShape?.children?.find((s) => s.id === sizeId);
        const isEnabled = currentSize?.checked;

        const updatedSizes = config.sizes.map((size) => {
          if (size.size_sku === currentSize?.sku && isEnabled) {
            return {
              ...size,
              shanks: size.shanks.map((shank) => {
                if (shank.shank_sku === shankSku) {
                  return { 
                    ...shank, 
                    config: {
                      ...shank.config,
                      [configSwitchLabel]: {
                        ...shank.config?.[configSwitchLabel],
                        padding: Math.max(0, Math.min(50, newValue))
                      }
                    }
                  };
                }
                return shank;
              }),
            };
          }
          return size;
        });

        return {
          ...config,
          sizes: updatedSizes,
        };
      }
      return config;
    });

    // Update mapping data
    const updatedMappingData = {
      ...state.mappingData,
      shapes: state.mappingData.shapes.map((shape) => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            children: shape.children.map((size) => {
              if (size.id === sizeId) {
                return {
                  ...size,
                  children: size.children.map(shank => {
                    if (shank.sku === shankSku) {
                      return { 
                        ...shank, 
                        config: {
                          ...shank.config,
                          [configSwitchLabel]: {
                            ...shank.config?.[configSwitchLabel],
                            padding: Math.max(0, Math.min(50, newValue))
                          }
                        }
                      };
                    }
                    return shank;
                  })
                };
              }
              return size;
            }),
          };
        }
        return shape;
      }),
    };

    dispatch({ type: ACTIONS.UPDATE_SHAPE_CONFIGS, payload: updatedConfigs });
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedMappingData });
  };

  const handleSizeShadowChange = (shapeId, sizeId, shankSku, newValue) => {
    // Update shape configs
    const updatedConfigs = state.shapeConfigs.map((config) => {
      const currentShape = state.mappingData.shapes.find((s) => s.id === shapeId);
      if (config.shape_sku === currentShape?.sku) {
        const currentSize = currentShape?.children?.find((s) => s.id === sizeId);
        const isEnabled = currentSize?.checked;

        const updatedSizes = config.sizes.map((size) => {
          if (size.size_sku === currentSize?.sku && isEnabled) {
            return {
              ...size,
              shanks: size.shanks.map((shank) => {
                if (shank.shank_sku === shankSku) {
                  return { 
                    ...shank, 
                    config: {
                      ...shank.config,
                      [configSwitchLabel]: {
                        ...shank.config?.[configSwitchLabel],
                        depth: newValue
                      }
                    }
                  };
                }
                return shank;
              }),
            };
          }
          return size;
        });

        return {
          ...config,
          sizes: updatedSizes,
        };
      }
      return config;
    });

    // Update mapping data
    const updatedMappingData = {
      ...state.mappingData,
      shapes: state.mappingData.shapes.map((shape) => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            children: shape.children.map((size) => {
              if (size.id === sizeId) {
                return {
                  ...size,
                  children: size.children.map(shank => {
                    if (shank.sku === shankSku) {
                      return { 
                        ...shank, 
                        config: {
                          ...shank.config,
                          [configSwitchLabel]: {
                            ...shank.config?.[configSwitchLabel],
                            depth: newValue
                          }
                        }
                      };
                    }
                    return shank;
                  })
                };
              }
              return size;
            }),
          };
        }
        return shape;
      }),
    };

    dispatch({ type: ACTIONS.UPDATE_SHAPE_CONFIGS, payload: updatedConfigs });
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedMappingData });
  };

  const handleRotationChange = (shapeId, sizeId, shankSku, newValue) => {
    // Update shape configs
    const updatedConfigs = state.shapeConfigs.map((config) => {
      const currentShape = state.mappingData.shapes.find((s) => s.id === shapeId);
      if (config.shape_sku === currentShape?.sku) {
        const currentSize = currentShape?.children?.find((s) => s.id === sizeId);
        const isEnabled = currentSize?.checked;

        const updatedSizes = config.sizes.map((size) => {
          if (size.size_sku === currentSize?.sku && isEnabled) {
            return {
              ...size,
              shanks: size.shanks.map((shank) => {
                if (shank.shank_sku === shankSku) {
                  return { 
                    ...shank, 
                    config: {
                      ...shank.config,
                      [configSwitchLabel]: {
                        ...shank.config?.[configSwitchLabel],
                        shape_rotation: newValue
                      }
                    }
                  };
                }
                return shank;
              }),
            };
          }
          return size;
        });

        return {
          ...config,
          sizes: updatedSizes,
        };
      }
      return config;
    });

    // Update mapping data
    const updatedMappingData = {
      ...state.mappingData,
      shapes: state.mappingData.shapes.map((shape) => {
        if (shape.id === shapeId) {
          return {
            ...shape,
            children: shape.children.map((size) => {
              if (size.id === sizeId) {
                return {
                  ...size,
                  children: size.children.map(shank => {
                    if (shank.sku === shankSku) {
                      return { 
                        ...shank, 
                        config: {
                          ...shank.config,
                          [configSwitchLabel]: {
                            ...shank.config?.[configSwitchLabel],
                            shape_rotation: newValue
                          }
                        }
                      };
                    }
                    return shank;
                  })
                };
              }
              return size;
            }),
          };
        }
        return shape;
      }),
    };

    dispatch({ type: ACTIONS.UPDATE_SHAPE_CONFIGS, payload: updatedConfigs });
    dispatch({ type: ACTIONS.SET_MAPPING_DATA, payload: updatedMappingData });
  };

  return (
    <div className={classes.root}>
      {state.mappingData?.shapes?.map((shape) => {
        const shapeConfig = state.shapeConfigs.find((config) => config.shape_sku === shape?.sku);

        return (
          <ShapeAccordion
            key={shape.id}
            shape={shape}
            shapeConfig={shapeConfig}
            expanded={expanded}
            onAccordionChange={handleAccordionChange}
            onShapeToggle={handleShapeToggle}
            onSizeToggle={handleSizeToggle}
            onShankToggle={handleShankToggle}
            onShapeScaleChange={handleShapeScaleChange}
            onSizeScaleChange={handleSizeScaleChange}
            onShapePaddingChange={handleShapePaddingChange}
            onSizePaddingChange={handleSizePaddingChange}
            onSizeShadowChange={handleSizeShadowChange}
            onRotationChange={handleRotationChange}
          />
        );
      })}
    </div>
  );
};

export default ApprovalMapping;
